import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";

/**
 * Checkbox multi-select picker for client / vendor / project links.
 *
 * Sibling of EmployeeMultiSelect: same centered portal popup, same reasons
 * (a transformed modal ancestor would clip a plain dropdown, and a Radix
 * modal's focus trap steals the search box's focus unless the capture-phase
 * guards below stop it). Selection order is preserved — callers rely on the
 * first id being the "primary" link the backend stores on the singular column.
 *
 * Lived inline in pages/Task/TaskCreate.tsx until TaskEdit needed it too.
 */
export type EntityMultiSelectOption = {
  id: string;
  label: string;
  meta?: string | null;
};

export function EntityMultiSelect({
  values,
  onChange,
  options,
  placeholder,
  singularLabel,
  pluralLabel,
  searchPlaceholder,
  emptyText,
  onAddNew,
  addLabel,
  ariaLabel,
}: {
  values: string[];
  onChange: (ids: string[]) => void;
  options: EntityMultiSelectOption[];
  placeholder: string;
  singularLabel: string;
  pluralLabel: string;
  searchPlaceholder: string;
  emptyText: string;
  // Optional: not every host has a create-new flow for the entity (TaskEdit
  // has no "add vendor" modal, for instance). Omit both and the button is
  // simply not rendered.
  onAddNew?: () => void;
  addLabel?: string;
  // Names the trigger for assistive tech and for tests, the way the Select it
  // replaced was named by its SelectTrigger's aria-label.
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const listboxId = React.useId();

  useEffect(() => {
    if (!open) return;
    const involvesPopup = (n: EventTarget | null) =>
      n instanceof Node && !!popupRef.current?.contains(n);
    const guard = (e: FocusEvent) => {
      if (involvesPopup(e.target) || involvesPopup(e.relatedTarget)) {
        e.stopPropagation();
      }
    };
    document.addEventListener("focusin", guard, true);
    document.addEventListener("focusout", guard, true);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("focusin", guard, true);
      document.removeEventListener("focusout", guard, true);
    };
  }, [open]);

  const selectedOptions = options.filter((option) => values.includes(option.id));
  const filteredOptions = options.filter((option) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${option.label} ${option.meta || ""}`.toLowerCase().includes(q);
  });
  const allSelected = options.length > 0 && options.every((option) => values.includes(option.id));

  const toggle = (id: string) => {
    if (values.includes(id)) {
      onChange(values.filter((value) => value !== id));
    } else {
      onChange([...values, id]);
    }
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : options.map((option) => option.id));
  };

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const triggerText =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length <= 2
      ? selectedOptions.map((option) => option.label).join(", ")
      : `${selectedOptions.length} ${pluralLabel} selected`;

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => {
          setQuery("");
          setOpen(true);
        }}
        className="flex h-10 w-full items-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-left text-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={`min-w-0 flex-1 truncate ${selectedOptions.length ? "text-gray-900" : "text-gray-400"}`}>
          {triggerText}
        </span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
      </button>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 pointer-events-auto"
            onMouseDown={close}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <div
              className="relative w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-150"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-2 py-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    autoComplete="off"
                    placeholder={searchPlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    close();
                  }}
                  className="shrink-0 text-gray-500 hover:text-gray-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleAll();
                  }}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border py-1.5 text-xs font-medium transition-all ${
                    allSelected
                      ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                      : "border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  {allSelected ? (
                    <>
                      <X className="h-3 w-3" /> Deselect all
                    </>
                  ) : (
                    <>
                      <Check className="h-3 w-3" /> Select all
                    </>
                  )}
                </button>
                {onAddNew && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      close();
                      onAddNew();
                    }}
                    className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                  >
                    {addLabel || "+ Add"}
                  </button>
                )}
              </div>

              <ul
                id={listboxId}
                role="listbox"
                aria-multiselectable="true"
                className="max-h-[210px] overflow-y-auto py-1"
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                {filteredOptions.length === 0 ? (
                  <li className="px-3 py-2 text-xs italic text-gray-400">
                    {query ? `No ${pluralLabel} match "${query}".` : emptyText}
                  </li>
                ) : (
                  filteredOptions.map((option) => {
                    const selected = values.includes(option.id);
                    return (
                      <li
                        key={option.id}
                        role="option"
                        aria-selected={selected}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          toggle(option.id);
                        }}
                        className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                          selected ? "bg-indigo-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {}}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate ${selected ? "font-medium text-indigo-700" : "text-gray-900"}`}>
                            {option.label}
                          </span>
                          {option.meta && (
                            <span className="block truncate text-[11px] text-gray-500">
                              {option.meta}
                            </span>
                          )}
                        </span>
                        {selected && <Check className="h-3.5 w-3.5 shrink-0 text-indigo-600" />}
                      </li>
                    );
                  })
                )}
              </ul>

              <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                <span className="text-gray-600">
                  {values.length} {values.length === 1 ? singularLabel : pluralLabel} selected
                </span>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    close();
                  }}
                  className="font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default EntityMultiSelect;
